import { ChevronRight } from 'lucide-react'

// Exported so other disclosure UI (e.g. the Matches-tab rung ladder) shares
// this single level→name/colour mapping instead of redefining it.
export const STAGES = [
  { level: 1, name: 'Anonymous Match', color: '#6B7280' },
  { level: 2, name: 'Aggregated Stats', color: '#6B7280' },
  { level: 3, name: 'Categorical Profile', color: '#6B7280' },
  { level: 4, name: 'Pattern Insights', color: '#F59E0B' },
  { level: 5, name: 'Structured Summary', color: '#3B82F6' },
  { level: 6, name: 'Full Collaboration', color: '#10B981' },
] as const

interface DisclosureLevelProps {
  currentLevel: number
  pendingAdvance: boolean
  onRequestAdvance: () => void
  compact?: boolean
}

export default function DisclosureLevel({
  currentLevel,
  pendingAdvance,
  onRequestAdvance,
  compact = false,
}: DisclosureLevelProps) {
  if (compact) {
    return (
      <div className="flex items-center gap-0.5">
        {STAGES.map((stage) => (
          <div
            key={stage.level}
            className="h-1.5 w-4 rounded-full"
            style={{
              backgroundColor: stage.level <= currentLevel ? stage.color : '#E5E7EB',
            }}
            title={stage.name}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Disclosure Level
        </span>
        <div className="flex items-center gap-2">
          {pendingAdvance && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 rounded">
              Pending
            </span>
          )}
          {currentLevel < 6 && !pendingAdvance && (
            <button
              onClick={onRequestAdvance}
              className="flex items-center gap-0.5 px-2 py-0.5 text-[11px] font-medium text-iflow-600 hover:text-iflow-700 hover:bg-iflow-50 rounded transition-colors"
            >
              Request Advance <ChevronRight size={12} />
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-1">
        {STAGES.map((stage) => {
          const active = stage.level <= currentLevel
          return (
            <div key={stage.level} className="flex-1 group relative">
              <div
                className="h-2 rounded-full transition-colors"
                style={{
                  backgroundColor: active ? stage.color : 'transparent',
                  border: active ? 'none' : `1.5px solid ${stage.color}40`,
                }}
              />
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 hidden group-hover:block z-10">
                <span className="whitespace-nowrap px-1.5 py-0.5 text-[10px] bg-gray-900 text-white rounded shadow">
                  {stage.level}. {stage.name}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex justify-between text-[10px] text-gray-400">
        <span>1 — {STAGES[0].name}</span>
        <span>{currentLevel}/6 — {STAGES[currentLevel - 1]?.name}</span>
      </div>
    </div>
  )
}
